import axios from 'axios';
import get from 'lodash.get';
import set from 'lodash.set';
import moment from 'moment';
import qs from 'qs';

import * as AuthConstants from './authentication/constants';
import LoginService from './authentication/login/login-service';
import LogoutService from './authentication/logout/logout-service';
import * as Cookies from './constants/cookies';
import { gitData } from './constants/git-data';
import { emptyObject } from './default-props/empty';
import { mfaRequiredSubject } from './global/step-up-authentication/account-verification-needed.subject';
import { checkResponseIsMfaNeeded, checkResponseIsEmailVerificationRequired } from './global/step-up-authentication/helpers';
import auth0Wrapper from './services/auth0/Auth0Wrapper';
import CookieService from './services/cookie-service';
import DropInRestrictionsService from './services/dropin-restrictions-service';
import LocalStorageService from './services/local-storage-service';
import LogService from './services/log-service';
import bulkload from './store/entities/entities.bulkload';
import ratePatcher from './store/entities/entities.rate-patcher';

const dateTimeList = [
  'holdUntilDateTime',
  'orderDateTime',
  'paidDateTime',
  'shipByDateTime',
  'createdDateTime',
  'modifiedDateTime',
  {
    key: 'value',
    route: '/api/SalesOrder/{salesOrderId}/setHoldUntilDate',
  },
  {
    key: 'value',
    route: '/api/SalesOrder/{salesOrderId}/setPaidDate',
  },
  'shipDateTime',
  'expectedDeliveryDateTime',
  'deliveryDate',
  'shipDate',
  'suggestedShipDate',
  'verificationUtc',
  'labelCreatedDateTime',
  'createDate',
  'orderDate',
  { route: '/api/ordergrid', key: 'orderedAfter' },
  { route: '/api/ordergrid', key: 'orderedBefore' },
  { route: '/api/ordergrid', key: 'paidAfter' },
  { route: '/api/ordergrid', key: 'paidBefore' },
];

/**
 *
 */
function objectToPaths(ob) {
  const validId = /^[a-z_$][a-z0-9_$]*$/i;
  const result = [];

  /**
   *
   */
  function doIt(data, s) {
    if (data && typeof data === 'object') {
      if (Array.isArray(data)) {
        for (let i = 0; i < data.length; i++) {
          doIt(data[i], `${s}[${i}]`);
        }
      } else {
        Object.keys(data).forEach((p) => {
          if (validId.test(p)) {
            doIt(data[p], `${s}.${p}`);
          } else {
            doIt(data[p], `${s}["${p}"]`);
          }
        });
      }
    } else {
      result.push(s);
    }
  }

  doIt(ob, '');
  return result;
}

const transformDates = (data) => {
  const paths = objectToPaths(data);

  paths.forEach((path) => {
    const shouldTransformDate = dateTimeList.reduce((isInList, dtCheck) => {
      if (isInList) {
        return isInList;
      }

      return path.endsWith(`.${dtCheck}`);
    }, false);

    if (shouldTransformDate) {
      const transformPath = path.substr(1);
      const date = get(data, transformPath);
      let isoTransformedDate;
      if (date) {
        try {
          const transformedDate = moment(date);
          if (!transformedDate.isValid()) {
            throw new Error(`Date is not valid ${date}`);
          }
          isoTransformedDate = transformedDate.toISOString();
          set(data, transformPath, isoTransformedDate);
        } catch (e) {
          LogService.logInfo('Error caught during date transformation', e);
        }
      }
    }
  });
};

const isUnauthorized = (response) => {
  return response && response.status === 401;
};

const handleUnauthorizedLogout = (response) => {
  const { location } = response.data;
  if (location) {
    CookieService.removeCookie(Cookies.SSHome);
    CookieService.setCookieValueWithExpiration(Cookies.SSHome, location.replaceAll('/', ''), 365 * 2);
    LocalStorageService.clear();
    window.location.href = location;
  } else {
    LogoutService.logout('/logout');
  }
};

const isMaintenance = (response) => {
  return response && response.status === 503;
};

const handleMaintenanceLogout = () => {
  return LogoutService.logout('/logout', AuthConstants.LogoutTypeMaintenance);
};

const customDefaultHeaders = {
  'git-version': gitData,
};

export const addCustomDefaultHeadersToAxiosRequests = (newHeaders = {}) => {
  Object.entries(newHeaders).map(([key, value]) => (customDefaultHeaders[key] = value));
};

let dispatch;
export const setDispatch = (newDispatch) => {
  dispatch = newDispatch;
};

/**
 * @param {object} [options]
 * @param {boolean} [options.isNativeAuth]
 */
export const setupAxios = (options = {}) => {
  axios.defaults.baseURL = '/api';

  axios.interceptors.request.use(
    async (config) => {
      const configWithUpdatedHeaders = {
        ...config,
        headers: config.headers ? { ...config.headers, ...customDefaultHeaders } : customDefaultHeaders,
      };

      if (!auth0Wrapper.isUsingAuth0Auth() || options.isNativeAuth) {
        // native auth, do nothing...
        return configWithUpdatedHeaders;
      }

      try {
        const accessToken = await auth0Wrapper.getAccessToken();

        return {
          ...configWithUpdatedHeaders,
          headers: {
            ...configWithUpdatedHeaders.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        };
      } catch {
        // WARNING: never log here using the http log service!!! (recursion death-spiral warning)
        // expected auth0 exception on e.error === 'login_required'

        // TODO: this is the end goal, but moving to this now will likely present a new unintended logout
        // Future state: await handleUnauthorizedLogout(error.response);
        // Future state: throw error;

        // Temporary state: do not log user out (mimics current behavior)
        // Allow currently loaded app to continue session until _lat* token expires and forces a full logout
        return configWithUpdatedHeaders;
      }
    },
    (error) => {
      throw error;
    },
  );

  axios.defaults.paramsSerializer = (queryParams) => {
    if (!queryParams) {
      return;
    }

    transformDates(queryParams);

    return qs.stringify(queryParams, {
      indicies: false,
      arrayFormat: 'repeat',
    });
  };

  axios.defaults.transformRequest = [
    function transformRequestDates(data, headers) {
      if (!data) {
        return;
      }

      transformDates(data);

      // eslint-disable-next-line
      headers['Content-Type'] = 'application/json; charset=utf-8';

      if (headers.ContentType && headers.ContentType === 'multipart/form-data') {
        return data;
      }

      return JSON.stringify(data);
    },
  ];

  axios.interceptors.response.use(
    (response) => {
      const renewToken = response.headers['renew-token'];
      if (renewToken) {
        LoginService.setAuthToken(renewToken);
      }

      if (dispatch) {
        ratePatcher(get(response, 'config.url'), get(response, 'data', emptyObject), dispatch);
        bulkload(get(response, 'config.url'), get(response, 'data', emptyObject), dispatch);
      }

      return response;
    },
    async (error) => {
      const isMfaRequired = checkResponseIsMfaNeeded(error);
      const isNativeVerificationRequired = checkResponseIsEmailVerificationRequired(error);

      const config = error?.config;

      if ((isMfaRequired || isNativeVerificationRequired) && config && !config.ss_isMfaRetry) {
        // prevent infinite loops
        config.ss_isMfaRetry = true;

        return new Promise((resolve, reject) => {
          mfaRequiredSubject.next({
            config,
            resolve,
            reject,
            url: config.url,
            method: config.method,
            isNative: isNativeVerificationRequired,
          });
        });
      }

      const { response } = error;

      if (isUnauthorized(response)) {
        await handleUnauthorizedLogout(response);
      } else if (isMaintenance(response)) {
        await handleMaintenanceLogout();
      } else if (DropInRestrictionsService.isResponseUnauthorizedDropInAction(response)) {
        DropInRestrictionsService.showDropinActionRestrictedMessage(response);
      }

      throw error;
    },
  );
};
